/**
 * PreviewTab Component
 *
 * Live preview of how the guide profile will appear to customers.
 * Shows a read-only version of the public profile using current profile data.
 *
 * Epic: MVP 1.21.3 - Guide Dashboard
 * Story: US-1.21.3-13: Live Profile Preview (3 SP)
 *
 * Props:
 * - profile: object with current profile data
 */

import React from 'react';
import PropTypes from 'prop-types';
import { MapPin as _MapPin, Star as _Star, Award, Shield, Briefcase, Users, Star } from 'lucide-react';
import { getGuidePhotoUrl } from '../../../utils/guideDataAdapters';
import Avatar from '../../common/Avatar';

export default function PreviewTab({ profile }) {
  const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Your Name';
  const displayName = profile.business_name || fullName;

  const coverPhotoUrl = getGuidePhotoUrl(profile, 'cover');
  const profilePhotoUrl = getGuidePhotoUrl(profile, 'profile');

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 bg-blue-50 border-b border-blue-200">
        <p className="text-sm text-blue-800 text-center">
          <strong>Preview Mode:</strong> This is how customers will see your profile
        </p>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Cover Photo */}
        {coverPhotoUrl ? (
          <div className="w-full h-64 bg-gray-200">
            <img
              src={coverPhotoUrl}
              alt="Cover"
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-full h-64 bg-gradient-to-r from-blue-500 to-blue-600" />
        )}

        {/* Profile Header */}
        <div className="px-6 pb-6">
          <div className="flex items-start space-x-6 -mt-16">
            {/* Profile Photo */}
            <Avatar
              src={profilePhotoUrl}
              name={fullName}
              size="3xl"
              className="flex-shrink-0 border-4 border-white shadow-lg"
            />

            {/* Name and Details */}
            <div className="flex-1 mt-16">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{displayName}</h1>
                  {profile.business_name && profile.first_name && (
                    <p className="text-lg text-gray-600 mt-1">
                      Guide: {fullName}
                    </p>
                  )}
                  {profile.primary_activity && (
                    <p className="text-sm text-gray-500 mt-1 flex items-center space-x-1">
                      <Briefcase className="w-4 h-4" />
                      <span>{profile.primary_activity} Specialist</span>
                    </p>
                  )}
                </div>

                <div className="text-right">
                  <div className="flex items-center space-x-1 text-yellow-500">
                    <Star className="w-5 h-5 fill-current" />
                    <span className="text-lg font-semibold text-gray-900">5.0</span>
                  </div>
                  <p className="text-sm text-gray-500">(No reviews yet)</p>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                {profile.experience_range && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900">{profile.experience_range}</p>
                    <p className="text-xs text-gray-600">Years Experience</p>
                  </div>
                )}
                {profile.specialties && profile.specialties.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900">{profile.specialties.length}</p>
                    <p className="text-xs text-gray-600">Specialties</p>
                  </div>
                )}
                {profile.service_radius_miles && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900">{profile.service_radius_miles}</p>
                    <p className="text-xs text-gray-600">Mile Radius</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 px-6 py-6 space-y-6">
          {/* About / Bio */}
          {profile.bio && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">About</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {profile.bio}
              </p>
            </div>
          )}

          {/* Service Description */}
          {profile.service_description && (
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Services</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {profile.service_description}
              </p>
            </div>
          )}

          {/* Specialties */}
          {profile.specialties && profile.specialties.length > 0 && (
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                <Award className="w-5 h-5 text-blue-600" />
                <span>Specialties</span>
              </h2>
              <div className="flex flex-wrap gap-2">
                {profile.specialties.map((specialty) => (
                  <span
                    key={specialty}
                    className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Credentials */}
          {(profile.license_number || profile.insurance_provider) && (
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                <Shield className="w-5 h-5 text-green-600" />
                <span>Credentials & Insurance</span>
              </h2>
              <div className="space-y-2">
                {profile.license_number && (
                  <div className="flex items-center space-x-2 text-sm">
                    <span className="px-2 py-1 bg-green-100 text-green-800 font-medium rounded">
                      Licensed
                    </span>
                    <span className="text-gray-600">
                      {profile.license_state ? `${profile.license_state} ` : ''}License #{profile.license_number}
                    </span>
                  </div>
                )}
                {profile.insurance_provider && (
                  <div className="flex items-center space-x-2 text-sm">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 font-medium rounded">
                      Insured
                    </span>
                    <span className="text-gray-600">
                      {profile.insurance_provider}
                      {profile.insurance_expiry && ` (Valid until ${new Date(profile.insurance_expiry).toLocaleDateString()})`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contact CTA */}
          <div className="border-t border-gray-200 pt-6">
            <button
              disabled
              className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-md opacity-50 cursor-not-allowed"
            >
              Contact {displayName}
            </button>
            <p className="mt-2 text-xs text-gray-500">
              This is a preview - the contact button will work on the live profile
            </p>
          </div>
        </div>

        {/* Empty State Messages */}
        {!profile.bio && !profile.service_description && !profile.specialties?.length && (
          <div className="px-6 py-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">Your profile is incomplete</p>
            <p className="text-sm text-gray-500 mt-1">
              Fill out the other tabs to create a compelling profile that attracts customers
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

PreviewTab.propTypes = {
  profile: PropTypes.object.isRequired,
};
